import { CHART_COLORS } from 'consts/consts';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { NO_RESPONSE_COLOR } from 'views/Widgets/Charts/chartColors';

export interface TrendBucket {
  key: string;
  label: string;
  denominator?: number;
  // Below the anonymity floor. Its percentages arrive zeroed, so plotting
  // them as-is would draw a truthful-looking 0% instead of a hidden point.
  suppressed?: boolean;
}

export interface TrendSeries {
  value_key: string;
  value_label: string;
  percentages: number[];
}

// One value of the question's domain, with how many answers each bucket got
// and the colour that value is drawn in elsewhere (doughnut, bars).
export interface TrendBreakdownSeries {
  value_key: string;
  value_label: string;
  counts: number[];
  color: string;
}

interface Props {
  buckets: TrendBucket[];
  series: TrendSeries[];
  // Shown in place of a value for a bucket held back by the anonymity floor.
  suppressedLabel: string;
  // Shown when a point has no answers behind it at all.
  emptyLabel: string;
  // Under a point's answers: who didn't answer, and how many of those invited
  // did - why the point's lines add up to less than 100%, as the doughnut
  // shows with its own "no response" slice.
  noResponseLabel: string;
  formatRespondedLabel: (answered: number, denominator: number) => string;
  // One row per value of the question's domain, shown on hovering a point.
  // Only the values actually answered at that point are listed.
  breakdown?: TrendBreakdownSeries[];
}

interface HoveredPoint {
  index: number;
  x: number;
  y: number;
  // Which lines the cursor is actually on - more than one when they overlap.
  datasetIndexes: number[];
}

const TooltipRow: FC<{ color: string; label: string; count: number }> = ({
  color,
  label,
  count,
}) => (
  <div className="d-flex align-items-center gap-2 py-1">
    <span
      style={{
        backgroundColor: color,
        width: '0.65rem',
        height: '0.65rem',
        borderRadius: '2px',
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
    <span className="flex-grow-1">{label}</span>
    <span className="fw-bold">{count}</span>
  </div>
);

// #RGB or #RRGGBB -> rgba(r, g, b, alpha), for a translucent fill under the line.
const hexToRgba = (hex: string, alpha: number): string => {
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Multi-series % favorable / % response line chart - used for any chart
// grouped by instance or by period. Matches the existing Line usage in
// DistributionProgress.tsx.
const SurveyTrendLineChart: FC<Props> = ({
  buckets,
  series,
  suppressedLabel,
  emptyLabel,
  noResponseLabel,
  formatRespondedLabel,
  breakdown,
}) => {
  const [hovered, setHovered] = useState<HoveredPoint | null>(null);

  // chart.js calls this on every tooltip frame, so it returns the previous
  // object unless something actually moved - a new object each time would
  // re-render on every frame.
  const handleTooltip = useCallback((model) => {
    if (!model || model.opacity === 0) {
      setHovered(null);
      return;
    }
    const points = model.dataPoints ?? [];
    const index = points[0]?.index;
    if (index == null) return;
    const datasetIndexes = points.map((p) => p.datasetIndex);
    const key = datasetIndexes.join(',');
    setHovered((prev) =>
      prev &&
      prev.index === index &&
      prev.x === model.caretX &&
      prev.datasetIndexes.join(',') === key
        ? prev
        : { index, x: model.caretX, y: model.caretY, datasetIndexes }
    );
  }, []);

  // Memoized so hovering - which sets state above - doesn't hand the chart a
  // new data/options pair on every render and restart it mid-tooltip.
  const data = useMemo(
    () => ({
      labels: buckets.map((b) => b.label),
      datasets: series.map((s, idx) => {
        // Scale colours when the caller supplies them (a star rating's 1..5
        // should read as a scale, not as arbitrary palette positions), the
        // categorical palette otherwise.
        const color =
          breakdown?.[idx]?.color ?? CHART_COLORS[idx % CHART_COLORS.length];
        return {
          label: s.value_label,
          // null, not 0: chart.js leaves a gap for it (spanGaps defaults to
          // false), so a hidden bucket breaks the line instead of dragging it
          // down to a zero nobody actually reported.
          data: s.percentages.map((percentage, i) =>
            buckets[i]?.suppressed ? null : percentage
          ),
          borderColor: color,
          backgroundColor: hexToRgba(color, 0.1),
          pointBackgroundColor: color,
          pointRadius: 3,
          borderWidth: 2,
          fill: false,
          lineTension: 0.2,
        };
      }),
    }),
    [buckets, series, breakdown]
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      legend: { display: series.length > 1, position: 'bottom' },
      tooltips: {
        // Only over a point itself, not anywhere along its x position.
        mode: 'point',
        intersect: true,
        // Always off, never toggled: the rows need a colour swatch each, which
        // chart.js only draws for rows mapping to a dataset, so the tooltip is
        // rendered below instead. Switching this per render risked chart.js
        // keeping the built-in one alive alongside it.
        enabled: false,
        custom: handleTooltip,
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              max: 100,
              callback: (value) => `${value}%`,
            },
          },
        ],
        xAxes: [{ gridLines: { display: false } }],
      },
    }),
    [series.length, handleTooltip]
  );

  const hoveredBucket = hovered ? buckets[hovered.index] : undefined;
  // Each line is one value, so a point reports the lines the cursor is on -
  // all of them where they overlap, rather than every value in the domain.
  const rowsAtPoint =
    hovered && breakdown
      ? hovered.datasetIndexes
          .map((datasetIndex) => breakdown[datasetIndex])
          .filter(Boolean)
      : [];
  // Everyone who answered at this point, whichever line they're on.
  const answeredAtPoint =
    hovered && breakdown
      ? breakdown.reduce(
          (sum, entry) => sum + (entry.counts[hovered.index] ?? 0),
          0
        )
      : 0;
  const denominatorAtPoint = hoveredBucket?.denominator ?? 0;
  const noResponseAtPoint = Math.max(0, denominatorAtPoint - answeredAtPoint);

  return (
    <div
      className="chart-wrapper position-relative"
      style={{ height: '260px' }}
      // chart.js only reports the hide while the pointer is still over the
      // canvas, so leaving it quickly can strand the tooltip on screen.
      onMouseLeave={() => setHovered(null)}
    >
      <Line data={data} options={options} />
      {breakdown && hovered && hoveredBucket && (
        <div
          className="bg-white border rounded shadow-sm px-3 py-2 small"
          style={{
            position: 'absolute',
            left: hovered.x,
            top: hovered.y,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            minWidth: '8rem',
            zIndex: 1,
          }}
        >
          <div className="fw-bold mb-1 text-center">{hoveredBucket.label}</div>
          {hoveredBucket.suppressed ? (
            <div className="text-muted">{suppressedLabel}</div>
          ) : rowsAtPoint.length === 0 ? (
            <div className="text-muted">{emptyLabel}</div>
          ) : (
            <>
              {rowsAtPoint.map((entry) => (
                <TooltipRow
                  key={entry.value_key}
                  color={entry.color}
                  label={entry.value_label}
                  count={entry.counts[hovered.index]}
                />
              ))}
              {noResponseAtPoint > 0 && (
                <TooltipRow
                  color={NO_RESPONSE_COLOR}
                  label={noResponseLabel}
                  count={noResponseAtPoint}
                />
              )}
              <div className="text-muted text-center mt-1">
                {formatRespondedLabel(answeredAtPoint, denominatorAtPoint)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(SurveyTrendLineChart);
